import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {UserGroupIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {useNavigate} from "react-router-dom";
import Dropdown from "components/Dropdown";
import {AudienceSegment} from "types/AudienceSegment";
import DeleteAudienceSegment from '../Delete';

function AudienceSegmentList() {
    const [page, setPage] = useState<number>(1);
    const result = useQuery(['audience-segments.all', {page}], () => ngage.audienceSegments.all(page), {keepPreviousData: true})
    
    const [deletingAudienceSegment, setDeletingAudienceSegment] = useState<AudienceSegment | null>(null);

    const navigate = useNavigate()
    
    // Prefetch next and previous pages
    prefetchNearbyPages('audience-segments.all', page, ngage.audienceSegments.all)

    // Prepare breadcrumbs
    const breadcrumbs = {'Engagement': '/', 'Segments': '/audience-segments'}

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/audience-segments/new')}>New</Button>

    return (
        <Page
          title="Segments"
          subTitle='Custom user groups based on specific criteria'
          actions={actions}
          breadcrumbs={breadcrumbs}
        >
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Added</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={3}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <UserGroupIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No audience segments</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((audienceSegment, index) => (
                        <TableRow key={audienceSegment.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        <Dropdown position='left'>
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/audience-segments/${audienceSegment.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/audience-segments/${audienceSegment.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingAudienceSegment(audienceSegment)}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <UserGroupIcon className="h-5 w-5 mr-2 text-gray-500" /> {audienceSegment.name}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{formattedDateTime(audienceSegment.created_at)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/audience-segments/${audienceSegment.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/audience-segments/${audienceSegment.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingAudienceSegment(audienceSegment)}
                                    ]}
                                </Dropdown>
                            </TableData>

                            {/* Delete confirmation modal */}
                            <DeleteAudienceSegment
                                audienceSegment={deletingAudienceSegment}
                                show={audienceSegment === deletingAudienceSegment}
                                onCancel={() => setDeletingAudienceSegment(null)}
                                onDelete={() => navigate('/audience-segments')}
                                onDeleteFail={() => setDeletingAudienceSegment(null)}
                            />
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default AudienceSegmentList;